import type { FirestoreError } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { log, logFile } from './logger'
import { Sentry } from './sentry'
import { isRestartInFlight } from './webViewRestart'

export const SHOW_ERROR_DETAILS = true

export type ErrorKind = 'network' | 'permission' | 'not-found' | 'unknown'

export interface ClassifiedError {
  kind: ErrorKind
  code: string
  message: string
  eventId?: string
  raw: Error
}

const NETWORK_CODES = new Set(['unavailable', 'cancelled', 'deadline-exceeded'])

export function classifyError(err: unknown): ClassifiedError {
  const code = (err as FirestoreError)?.code ?? 'unknown'
  // An OTA restart terminates Firestore on purpose, aborting every read in
  // flight; one line per read would bury the log around the restart.
  const abortedByRestart = code === 'aborted' && isRestartInFlight()
  if (!abortedByRestart) log('errors', 'classifyError', { error: String(err) })
  const eventId = abortedByRestart ? undefined : Sentry.captureException(err)
  let kind: ErrorKind = 'unknown'
  let message: string = fbt('Something went wrong.', 'Fallback error message when an operation fails for an unknown reason')

  if (NETWORK_CODES.has(code)) {
    kind = 'network'
    message = fbt(
      "Couldn't reach Faro. Check your connection and try again.",
      'Error message when an operation fails because the network is unavailable',
    )
  } else if (code === 'permission-denied') {
    kind = 'permission'
    message = fbt("You don't have access to this.", 'Error message when an operation is denied by permissions')
  } else if (code === 'not-found') {
    kind = 'not-found'
    message = fbt('This is no longer here.', 'Error message when the item an operation targets was deleted')
  }

  const classified: ClassifiedError = { kind, code, message, eventId, raw: err as Error }
  if (!abortedByRestart) logFile('error', `${kind}: ${code}`, {
    message: (err as Error)?.message ?? String(err),
    ...(eventId && { eventId }),
  })
  return classified
}

export function formatErrorMessage(err: ClassifiedError): string {
  if (!SHOW_ERROR_DETAILS) return err.message
  const ref = err.eventId ? ` · ref ${err.eventId.slice(0, 8)}` : ''
  return `${err.message} (${err.code}${ref})`
}
